import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  CheckCircle, 
  Clock,
  ClipboardList,
  Users,
  Award,
  Shield,
  Sparkles,
  Eye,
  Wrench as Tools,
  Star,
  UserPlus,
  TrendingUp,
  Phone,
  Mail,
  MessageCircle,
  Euro,
  Calendar,
  MapPin,
  Target,
  BarChart3
} from 'lucide-react';

const LearnMoreCombined = ({ onBack, onJoinProgram, onPartnerLogin, onBookNow }) => {
  const { language, t } = useLanguage();

  // Work Process Content
  const getWorkProcessContent = () => {
    if (language === 'sq') {
      return {
        title: 'Si Punojmë Ne',
        subtitle: 'Metodologjia jonë profesionale për cilësi të garantuar',
        steps: [
          {
            title: 'Konsultimi Fillestar',
            description: 'Vlerësojmë nevojat tuaja dhe krijojmë një plan të personalizuar.',
            details: ['Inspektimi falas i vendit', 'Ofertë e detajuar', 'Planifikimi i kohës'],
            duration: '15-30 min',
            icon: ClipboardList,
            color: 'from-blue-500 to-cyan-500'
          },
          {
            title: 'Përgaditja Profesionale',
            description: 'Pajisemi me mjetet dhe produktet më të mira ekologjike.',
            details: ['Pajisje profesionale', 'Produkte miqësore me mjedisin', 'Siguria e plotë'],
            duration: '10-15 min',
            icon: Tools,
            color: 'from-emerald-500 to-teal-500'
          },
          {
            title: 'Zbatimi i Punës',
            description: 'Kryejmë shërbimin me standarde ndërkombëtare cilësie.',
            details: ['Metodologji të provuara', 'Kontroll cilësie', 'Komunikim i vazhdueshëm'],
            duration: '1-8 orë',
            icon: Sparkles,
            color: 'from-purple-500 to-pink-500'
          },
          {
            title: 'Kontrolli Final',
            description: 'Verifikojmë që gjithçka është e përfunduar me cilësi maksimale.',
            details: ['Inspektim final', 'Feedback i klientit', 'Garanci 100%'],
            duration: '10-20 min',
            icon: Eye,
            color: 'from-orange-500 to-red-500'
          }
        ]
      };
    } else if (language === 'de-ch') {
      return {
        title: 'Wie mir schaffe',
        subtitle: 'Üsi professionelli Methodik für garantierti Qualität',
        steps: [
          {
            title: 'Erschti Beratig',
            description: 'Mir bewärte eui Bedürfniss und erstelle en personalisierte Plan.',
            details: ['Gratis Ortstermin', 'Detailliertes Agebot', 'Optimali Zytplanig'],
            duration: '15-30 Min',
            icon: ClipboardList,
            color: 'from-blue-500 to-cyan-500'
          },
          {
            title: 'Professionelli Vorbereitig',
            description: 'Mir rüste üs mit de beschte Werkzüüg und umwältfreundliche Prodükt uus.',
            details: ['Professionelli Usrüstig', 'Umwältfreundlichi Prodükt', 'Vollständigi Sicherheit'],
            duration: '10-15 Min',
            icon: Tools,
            color: 'from-emerald-500 to-teal-500'
          },
          {
            title: 'Usfüehrig vo dr Arbeit',
            description: 'Mir füehre de Service mit internationali Qualitäts-Standards dure.',
            details: ['Bewährti Methode', 'Qualitäts-Kontrolle', 'Laufendi Kommunikation'],
            duration: '1-8 Stunde',
            icon: Sparkles,
            color: 'from-purple-500 to-pink-500'
          },
          {
            title: 'Schluss-Kontrolle',
            description: 'Mir prüefe dass alles mit höchster Qualität fertig isch.',
            details: ['Final Inspektion', 'Chunde-Feedback', '100% Garanti'],
            duration: '10-20 Min',
            icon: Eye,
            color: 'from-orange-500 to-red-500'
          }
        ]
      };
    } else if (language === 'de') {
      return {
        title: 'Wie wir arbeiten',
        subtitle: 'Unsere professionelle Methodik für garantierte Qualität',
        steps: [
          {
            title: 'Erstberatung',
            description: 'Wir bewerten Ihre Bedürfnisse und erstellen einen personalisierten Plan.',
            details: ['Kostenloser Ortstermin', 'Detailliertes Angebot', 'Optimale Zeitplanung'],
            duration: '15-30 Min',
            icon: ClipboardList,
            color: 'from-blue-500 to-cyan-500'
          },
          {
            title: 'Professionelle Vorbereitung',
            description: 'Wir rüsten uns mit den besten Werkzeugen und umweltfreundlichen Produkten aus.',
            details: ['Professionelle Ausrüstung', 'Umweltfreundliche Produkte', 'Vollständige Sicherheit'],
            duration: '10-15 Min',
            icon: Tools,
            color: 'from-emerald-500 to-teal-500'
          },
          {
            title: 'Ausführung der Arbeit',
            description: 'Wir führen den Service mit internationalen Qualitätsstandards durch.',
            details: ['Bewährte Methoden', 'Qualitätskontrolle', 'Laufende Kommunikation'],
            duration: '1-8 Stunden',
            icon: Sparkles,
            color: 'from-purple-500 to-pink-500'
          },
          {
            title: 'Abschlusskontrolle',
            description: 'Wir überprüfen, dass alles mit höchster Qualität fertiggestellt ist.',
            details: ['Abschlussinspektion', 'Kunden-Feedback', '100% Garantie'],
            duration: '10-20 Min',
            icon: Eye,
            color: 'from-orange-500 to-red-500'
          }
        ]
      };
    } else {
      return {
        title: 'How We Work',
        subtitle: 'Our professional methodology for guaranteed quality',
        steps: [
          {
            title: 'Initial Consultation',
            description: 'We assess your needs and create a personalized plan.',
            details: ['Free on-site inspection', 'Detailed quote', 'Optimal time planning'],
            duration: '15-30 min',
            icon: ClipboardList,
            color: 'from-blue-500 to-cyan-500'
          },
          {
            title: 'Professional Preparation',
            description: 'We equip ourselves with the best tools and eco-friendly products.',
            details: ['Professional equipment', 'Eco-friendly products', 'Complete safety'],
            duration: '10-15 min',
            icon: Tools,
            color: 'from-emerald-500 to-teal-500'
          },
          {
            title: 'Work Execution',
            description: 'We perform the service with international quality standards.',
            details: ['Proven methods', 'Quality control', 'Continuous communication'],
            duration: '1-8 hours',
            icon: Sparkles,
            color: 'from-purple-500 to-pink-500'
          },
          {
            title: 'Final Control',
            description: 'We verify that everything is completed with maximum quality.',
            details: ['Final inspection', 'Customer feedback', '100% guarantee'],
            duration: '10-20 min',
            icon: Eye,
            color: 'from-orange-500 to-red-500'
          }
        ]
      };
    }
  };

  // Pflichtenheft Content
  const getPflichtenheftContent = () => {
    if (language === 'sq') {
      return {
        title: 'Standardet dhe Kërkesat Tona',
        subtitle: 'Rregullat profesionale që garantojnë cilësinë e shërbimit',
        sections: [
          {
            title: 'Standardet e Cilësisë',
            items: [
              'Certifikime profesionale të pastrimit',
              'Produkte pastrimi të certifikuara ekologjike',
              'Kontroll cilësie në çdo hap të punës',
              'Garanci 100% për të gjitha shërbimet'
            ]
          },
          {
            title: 'Kërkesat e Sigurísë',
            items: [
              'Sigurimi i plotë i punëtorëve',
              'Pajisje mbrojtëse për të gjithë stafin',
              'Kontrolle të rregullta të sigurisë',
              'Protokolle emergjence të aprovuara'
            ]
          },
          {
            title: 'Standardet e Kohës',
            items: [
              'Respektimi i plotë i orareve të planifikuara',
              'Komunikim paraprak për çdo ndryshim',
              'Fleksibilitet sipas nevojave të klientit',
              'Shërbim 24/7 për raste urgjente'
            ]
          }
        ]
      };
    } else if (language === 'de-ch') {
      return {
        title: 'Üsi Standards und Aforderige',
        subtitle: 'Professionelli Regle wo d Qualität garantiere',
        sections: [
          {
            title: 'Qualitäts-Standards',
            items: [
              'Schwizer Qualitäts-Standards im Putze',
              'Zertifizirti ökologischi Putz-Prodükt',
              'Qualitäts-Kontrolle bi jedem Arbeitsschritt',
              '100% Garanti für alli Services'
            ]
          },
          {
            title: 'Sicherheits-Aforderige',
            items: [
              'Vollständigi Versicherig vo alle Mitarbeiter',
              'Schutz-Usrüstig für s ganze Team',
              'Regelmässigi Sicherheits-Kontrolle',
              'Genehmigti Notfall-Protokoll'
            ]
          },
          {
            title: 'Zyt-Standards',
            items: [
              'Vollständigi Iihaltig vo planierte Zyite',
              'Vorab-Kommunikation bi Änderige',
              'Flexibilität nach Chunde-Bedürfniss',
              '24/7 Service für Notfäll'
            ]
          }
        ]
      };
    } else if (language === 'de') {
      return {
        title: 'Unsere Standards und Anforderungen',
        subtitle: 'Professionelle Regeln, die Qualität garantieren',
        sections: [
          {
            title: 'Qualitätsstandards',
            items: [
              'Internationale ISO 9001 Zertifizierung',
              'Zertifizierte ökologische Reinigungsprodukte',
              'Qualitätskontrolle bei jedem Arbeitsschritt',
              '100% Garantie für alle Dienstleistungen'
            ]
          },
          {
            title: 'Sicherheitsanforderungen',
            items: [
              'Vollständige Versicherung aller Mitarbeiter',
              'Schutzausrüstung für das gesamte Team',
              'Regelmäßige Sicherheitskontrollen',
              'Genehmigte Notfallprotokolle'
            ]
          },
          {
            title: 'Zeitstandards',
            items: [
              'Vollständige Einhaltung geplanter Zeiten',
              'Vorabkommunikation bei Änderungen',
              'Flexibilität nach Kundenbedürfnissen',
              '24/7 Service für Notfälle'
            ]
          }
        ]
      };
    } else {
      return {
        title: 'Our Standards and Requirements',
        subtitle: 'Professional rules that guarantee quality',
        sections: [
          {
            title: 'Quality Standards',
            items: [
              'International ISO 9001 certification',
              'Certified ecological cleaning products',
              'Quality control at every work step',
              '100% guarantee for all services'
            ]
          },
          {
            title: 'Safety Requirements',
            items: [
              'Full insurance for all employees',
              'Protective equipment for entire staff',
              'Regular safety inspections',
              'Approved emergency protocols'
            ]
          },
          {
            title: 'Time Standards',
            items: [
              'Complete adherence to planned schedules',
              'Prior communication for any changes',
              'Flexibility according to customer needs',
              '24/7 service for emergencies'
            ]
          }
        ]
      };
    }
  };

  // Partner Hero Content
  const getPartnerHeroContent = () => {
    // Removed partner content as requested
    return null;
  };

  const workProcess = getWorkProcessContent();
  const pflichtenheft = getPflichtenheftContent();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Back Button */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'sq' ? 'Kthehu' : language === 'de-ch' ? 'Zrugg' : language === 'de' ? 'Zurück' : 'Back'}
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        
        {/* How Booking Works Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'sq' ? 'Si Funksionon Rezervimi' : 
               language === 'de-ch' ? 'Wie d Buchig funktioniert' : 
               language === 'de' ? 'Wie die Buchung funktioniert' : 
               'How Booking Works'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'sq' ? 'Procesi i thjeshtë për të rezervuar shërbimet tuaja të pastrimit në vetëm disa klikime' :
               language === 'de-ch' ? 'De eifachi Prozess zum eui Putz-Services z buche mit nur wenige Klicks' :
               language === 'de' ? 'Der einfache Prozess zur Buchung Ihrer Reinigungsdienstleistungen in nur wenigen Klicks' :
               'The simple process to book your cleaning services in just a few clicks'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: language === 'sq' ? 'Zgjidhni Shërbimin' : 
                       language === 'de-ch' ? 'Service uswähle' : 
                       language === 'de' ? 'Service auswählen' : 
                       'Choose Service',
                description: language === 'sq' ? 'Zgjidhni llojin e shërbimit që ju duhet nga lista jonë e plotë' :
                            language === 'de-ch' ? 'Wähled de Service-Typ wo ihr bruched us üsere vollständige Liste' :
                            language === 'de' ? 'Wählen Sie den Servicetyp, den Sie benötigen, aus unserer vollständigen Liste' :
                            'Select the type of service you need from our complete list',
                details: [
                  language === 'sq' ? 'Shërbime rezidenciale' : language === 'de-ch' ? 'Wohnigs-Services' : language === 'de' ? 'Wohnungsreinigung' : 'Residential services',
                  language === 'sq' ? 'Shërbime komerciale' : language === 'de-ch' ? 'Gwerblichs Services' : language === 'de' ? 'Gewerbliche Dienste' : 'Commercial services', 
                  language === 'sq' ? 'Shërbime të specializuara' : language === 'de-ch' ? 'Spezialisierti Services' : language === 'de' ? 'Spezialisierte Dienste' : 'Specialized services'
                ],
                icon: Target,
                color: 'from-blue-500 to-cyan-500'
              },
              {
                title: language === 'sq' ? 'Plotësoni Detajet' : 
                       language === 'de-ch' ? 'Details iiträge' : 
                       language === 'de' ? 'Details eingeben' : 
                       'Enter Details',
                description: language === 'sq' ? 'Plotësoni informacionet tuaja dhe specifikimet e shërbimit' :
                            language === 'de-ch' ? 'Fülled eui Informatione und Service-Spezifikatione uus' :
                            language === 'de' ? 'Geben Sie Ihre Informationen und Service-Spezifikationen ein' :
                            'Fill in your information and service specifications',
                details: [
                  language === 'sq' ? 'Informacione personale' : language === 'de-ch' ? 'Persönlichi Informatione' : language === 'de' ? 'Persönliche Informationen' : 'Personal information',
                  language === 'sq' ? 'Data dhe ora' : language === 'de-ch' ? 'Datum und Zyt' : language === 'de' ? 'Datum und Zeit' : 'Date and time',
                  language === 'sq' ? 'Kërkesat speciale' : language === 'de-ch' ? 'Speziellis Wünsch' : language === 'de' ? 'Spezielle Anforderungen' : 'Special requirements'
                ],
                icon: ClipboardList,
                color: 'from-emerald-500 to-teal-500'
              },
              {
                title: language === 'sq' ? 'Konfirmoni Rezervimin' : 
                       language === 'de-ch' ? 'Buchig bestätige' : 
                       language === 'de' ? 'Buchung bestätigen' : 
                       'Confirm Booking',
                description: language === 'sq' ? 'Rishikoni dhe konfirmoni rezervimin tuaj me detajet e çmimit' :
                            language === 'de-ch' ? 'Prüefed und bestätiged eui Buchig mit Priis-Details' :
                            language === 'de' ? 'Überprüfen und bestätigen Sie Ihre Buchung mit Preisdetails' :
                            'Review and confirm your booking with pricing details',
                details: [
                  language === 'sq' ? 'Çmimi transparent' : language === 'de-ch' ? 'Transparenti Priis' : language === 'de' ? 'Transparente Preise' : 'Transparent pricing',
                  language === 'sq' ? 'Konfirmim i menjëhershëm' : language === 'de-ch' ? 'Sofortigi Bestätigung' : language === 'de' ? 'Sofortige Bestätigung' : 'Instant confirmation',
                  language === 'sq' ? 'Email konfirmimi' : language === 'de-ch' ? 'Bestätigung-Email' : language === 'de' ? 'Bestätigungs-E-Mail' : 'Confirmation email'
                ],
                icon: CheckCircle,
                color: 'from-purple-500 to-pink-500'
              },
              {
                title: language === 'sq' ? 'Merrni Shërbimin' : 
                       language === 'de-ch' ? 'Service übercho' : 
                       language === 'de' ? 'Service erhalten' : 
                       'Get Service',
                description: language === 'sq' ? 'Ekipi ynë profesional do të vijë në kohën e caktuar' :
                            language === 'de-ch' ? 'Üses professionells Team chunnt zur vereinbarte Zyt' :
                            language === 'de' ? 'Unser professionelles Team kommt zur vereinbarten Zeit' :
                            'Our professional team will arrive at the scheduled time',
                details: [
                  language === 'sq' ? 'Ekip i trajnuar' : language === 'de-ch' ? 'Uusbildets Team' : language === 'de' ? 'Geschultes Team' : 'Trained team',
                  language === 'sq' ? 'Pajisje profesionale' : language === 'de-ch' ? 'Professionelli Usrüstig' : language === 'de' ? 'Professionelle Ausrüstung' : 'Professional equipment',
                  language === 'sq' ? 'Garanci cilësie' : language === 'de-ch' ? 'Qualitäts-Garanti' : language === 'de' ? 'Qualitätsgarantie' : 'Quality guarantee'
                ],
                icon: Star,
                color: 'from-orange-500 to-red-500'
              }
            ].map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="relative overflow-hidden">
                  <div className={`h-2 bg-gradient-to-r ${step.color}`}></div>
                  <CardHeader className="text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-lg">{step.title}</CardTitle>
                    <Badge variant="secondary" className="mt-2">
                      {language === 'sq' ? `Hapi ${index + 1}` : 
                       language === 'de-ch' ? `Schritt ${index + 1}` : 
                       language === 'de' ? `Schritt ${index + 1}` : 
                       `Step ${index + 1}`}
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4 text-center">{step.description}</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={onBookNow} 
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-4 text-lg rounded-full shadow-lg transform hover:scale-105 transition-all duration-300"
            >
              <Calendar className="h-6 w-6 mr-3" />
              {language === 'sq' ? 'Filloni Rezervimin' : 
               language === 'de-ch' ? 'Buchig starte' : 
               language === 'de' ? 'Buchung starten' : 
               'Start Booking'}
            </Button>
          </div>
        </section>

        {/* How We Work Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{workProcess.title}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{workProcess.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workProcess.steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="relative overflow-hidden">
                  <div className={`h-2 bg-gradient-to-r ${step.color}`}></div>
                  <CardHeader className="text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-lg">{step.title}</CardTitle>
                    <Badge variant="secondary" className="mt-2">{step.duration}</Badge>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4 text-center">{step.description}</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Pflichtenheft Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{pflichtenheft.title}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{pflichtenheft.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pflichtenheft.sections.map((section, index) => (
              <Card key={index} className="h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Shield className="h-5 w-5 text-emerald-600" />
                    </div>
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>


        {/* Call to Action Section */}
        <section className="mb-20">
          <Card className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold mb-4">
                {language === 'sq' ? 'Gati të Filloni?' : 
                 language === 'de-ch' ? 'Bereit azfange?' : 
                 language === 'de' ? 'Bereit anzufangen?' : 
                 'Ready to Get Started?'}
              </h2>
              <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
                {language === 'sq' ? 'Kontaktoni me ne sot dhe merrni një ofertë falas për shërbimet tuaja të pastrimit.' : 
                 language === 'de-ch' ? 'Kontaktier üs hüt und hol dir es gratis Agebot für dini Putz-Services.' : 
                 language === 'de' ? 'Kontaktieren Sie uns heute und erhalten Sie ein kostenloses Angebot für Ihre Reinigungsdienstleistungen.' : 
                 'Contact us today and get a free quote for your cleaning services.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={onBookNow} 
                  className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-3"
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  {language === 'sq' ? 'Rezervo Tani' : 
                   language === 'de-ch' ? 'Jetzt buche' : 
                   language === 'de' ? 'Jetzt buchen' : 
                   'Book Now'}
                </Button>
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-3"
                  onClick={() => window.open(`https://wa.me/41767669430`, '_blank')}
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  WhatsApp
                </Button>
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-3"
                  onClick={() => window.open(`tel:+41767669430`, '_blank')}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  +41 76 766 94 30
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Partner Program Section */}
        <section className="mb-20">
          <div className="bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 rounded-2xl overflow-hidden">
            <div className="p-12 text-center text-white">
              <div className="flex items-center justify-center mb-8">
                <UserPlus className="h-12 w-12 text-blue-400 mr-4" />
                <h2 className="text-4xl md:text-5xl font-bold">
                  {language === 'sq' ? 'Programi Partner' : 
                   language === 'de-ch' ? 'Partner Programm' : 
                   language === 'de' ? 'Partner Programm' : 
                   'Partner Program'}
                </h2>
              </div>
              <p className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto">
                {language === 'sq' ? 'Bashkohuni me ekipin tonë si Bashkepuntor profesional' :
                 language === 'de-ch' ? 'Schliesse di üsem Team aa als professionelle Mitarbeiter' :
                 language === 'de' ? 'Schließen Sie sich unserem Team an als professioneller Mitarbeiter' :
                 'Join our team as a professional collaborator'}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
                <Button 
                  size="lg" 
                  onClick={onJoinProgram}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-8 text-lg rounded-full shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <UserPlus className="mr-3 h-6 w-6" />
                  {language === 'sq' ? 'Zbulo Programin Partner' :
                   language === 'de-ch' ? 'Partner Programm entdecke' :
                   language === 'de' ? 'Partner Programm entdecken' :
                   'Discover Partner Program'}
                </Button>
                
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={onPartnerLogin}
                  className="border-white text-white hover:bg-white/10 backdrop-blur-sm py-4 px-8 text-lg rounded-full"
                >
                  <Award className="mr-3 h-6 w-6" />
                  {language === 'sq' ? 'Kyçuni si Partner' :
                   language === 'de-ch' ? 'Partner Aamäldig' :
                   language === 'de' ? 'Partner Anmeldung' :
                   'Partner Login'}
                </Button>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default LearnMoreCombined;